import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface CollapsibleSectionProps {
  /** Section title shown in the header. */
  title: string;
  /** Item count rendered as a badge next to the title. */
  count: number;
  /** Visual tone — `danger` colors the title red and fills the badge. */
  tone?: 'danger' | 'neutral';
  /** Whether the section starts expanded. Ephemeral — not persisted. */
  defaultOpen?: boolean;
  /** Section body, rendered only while expanded. */
  children: ReactNode;
}

const titleToneStyles: Record<
  NonNullable<CollapsibleSectionProps['tone']>,
  string
> = {
  danger: 'text-danger',
  neutral: 'text-foreground',
};

const badgeToneStyles: Record<
  NonNullable<CollapsibleSectionProps['tone']>,
  string
> = {
  danger: 'bg-danger text-background',
  neutral: 'bg-card border border-border text-foreground',
};

export function CollapsibleSection({
  title,
  count,
  tone = 'neutral',
  defaultOpen = true,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <section className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex items-center justify-between w-full bg-transparent border-none p-0 cursor-pointer text-left"
      >
        <span className="flex items-center gap-2">
          <span className={`text-xl font-bold ${titleToneStyles[tone]}`}>
            {title}
          </span>
          <span
            className={[
              'inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full text-xs',
              badgeToneStyles[tone],
            ].join(' ')}
          >
            {count}
          </span>
        </span>
        <span className="inline-flex items-center justify-center size-10 text-icon-default">
          <Chevron size={24} />
        </span>
      </button>

      {open && (
        <div
          id={bodyId}
          role="region"
          aria-label={title}
        >
          {children}
        </div>
      )}
    </section>
  );
}
